import { COURSE_MODULES } from 'constants/driving-elearning.constant';
import { PATH } from 'constants/path';
import useMediaQuery from 'hooks/useMediaQuery';
import React, { useEffect, useState } from 'react';
import { FaBookReader } from 'react-icons/fa';
import { IoMdEye } from 'react-icons/io';
import { MdCheckCircle, MdCheckCircleOutline, MdLink, MdList, MdOutlineForum, MdPlayArrow, MdQuiz } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { countModulesByType } from 'utils/commonUtils';
import { appendTokenToUrl } from 'utils/elearning.utils';
import axiosClient from 'api/axiosClient';
import {
  PrimaryButton,
  colors,
  radius,
  shadow,
  spacing,
  typography,
} from 'design-system';
const {
  Card,
  Row,
  Col,
  Collapse,
  ListGroup,
  Badge,
} = require('react-bootstrap');

// Resolve src cho ảnh course. BE (proxyGetMyCourses) thêm field
// `courseimageProxy` = relative `/elearning/book/pluginfile?...&sig=`
// (giữ `courseimage` gốc Moodle URL để backward-compat với FE cũ).
//   1. courseimageProxy có → prepend axios baseURL (FE/BE khác host: nếu
//      để relative, browser resolve theo FE host → rơi vào CRA shell → vỡ).
//   2. Không có proxy (BE cũ chưa deploy) → courseimage Moodle pluginfile
//      → appendTokenToUrl với moodleToken nếu còn (tab pre-migration);
//      null token thì để nguyên (ảnh vỡ — đúng trạng thái cũ, không crash).
//   3. Theme image / data URI → dùng nguyên.
function resolveCourseImageSrc(course, moodleToken) {
  const proxy = course?.courseimageProxy;
  if (proxy && typeof proxy === 'string') {
    const apiBase = axiosClient.defaults.baseURL || '';
    return apiBase ? `${apiBase}${proxy}` : proxy;
  }
  const img = course?.courseimage;
  if (!img) return img;
  if (img.includes('pluginfile.php') && moodleToken) {
    return appendTokenToUrl(img, moodleToken);
  }
  return img;
}

const CourseCard = ({ course, courseContent }) => {
  const moodleToken = localStorage.getItem('moodleToken');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [moduleCount, setModuleCount] = useState({});
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const count = countModulesByType(courseContent);
    setModuleCount(count);
  }, [courseContent]);

  // `undefined` = layout CHƯA fetch xong nội dung course này → đang tải thật.
  // `[]` = đã fetch xong nhưng rỗng (course không có nội dung, hoặc call bị
  // 429/timeout và layout trả [] fallback) → KHÔNG kẹt loading mãi nữa, cho
  // bấm nút (nội dung trống) thay vì quay vô tận. Trước đây `!courseContent
  // ?.length` coi cả [] lẫn undefined là loading → vài course bị 429 kẹt mãi.
  const isContentLoading = courseContent == null;

  return (
    <CardWrap>
      <Card.Body>
        <Row>
          <Col xs={isMobile ? 0 : 2} className='text-center'>
            {(course?.courseimageProxy || course?.courseimage) && (
              <CourseImage
                className={isMobile ? 'mb-2' : ''}
                src={resolveCourseImageSrc(course, moodleToken)}
                alt={course?.fullname}
              />
            )}
          </Col>
          <Col xs={isMobile ? 12 : 10}>
            <Row>
              <Col>
                <CourseTitle>{course.fullnamedisplay}</CourseTitle>
                <CourseSummary
                  dangerouslySetInnerHTML={{ __html: course.summary }}
                />
              </Col>
              <Col className='text-end'>
                {Object.keys(moduleCount)?.map((mod) => (
                  <ModuleBadge key={mod} pill>
                    <span>{moduleCount[mod]}</span>
                    <span>{COURSE_MODULES[mod]}</span>
                  </ModuleBadge>
                ))}
              </Col>
            </Row>
            <PrimaryButton
              variant='ghost'
              size='sm'
              loading={isContentLoading}
              loadingText='Đang tải nội dung...'
              disabled={isContentLoading}
              onClick={() => setOpen(!open)}
              aria-controls={`lessons-${course.id}`}
              aria-expanded={open}
            >
              <MdList /> {open ? 'Ẩn nội dung' : 'Xem nội dung'}
            </PrimaryButton>

            <Collapse in={open}>
              <div id={`lessons-${course.id}`}>
                <LessonList>
                  {courseContent?.map((lesson) => (
                    <LessonList.Item key={lesson?.id}>
                      <LessonSectionTitle>{lesson?.name}</LessonSectionTitle>
                      {lesson.modules?.map((mod) => (
                        <ModuleRow key={mod?.id}>
                          <ModuleNameCol xs md={9}>
                            <ModuleNameInner>
                              <span>
                                {mod?.completiondata?.isoverallcomplete &&
                                  mod?.completiondata?.hascompletion && (
                                    <MdCheckCircle
                                      style={{ color: colors.success }}
                                      size={20}
                                    />
                                  )}
                                {!mod?.completiondata?.isoverallcomplete &&
                                  mod?.completiondata?.hascompletion && (
                                    <MdCheckCircleOutline
                                      style={{ color: colors.gray500 }}
                                      size={20}
                                    />
                                  )}
                              </span>
                              <ModuleName>{mod?.name}</ModuleName>
                            </ModuleNameInner>
                          </ModuleNameCol>
                          <ModuleActionCol xs='auto' md={3}>
                            {mod.modname === 'forum' && (
                              <PrimaryButton
                                variant='ghost'
                                size='sm'
                                onClick={() =>
                                  navigate(
                                    `${PATH.ELEARNING.STUDENT.FORUM.replace(
                                      ':id',
                                      mod?.instance
                                    )}?m=${mod?.id}`
                                  )
                                }
                              >
                                <MdOutlineForum /> Thảo luận
                              </PrimaryButton>
                            )}
                            {mod.modname === 'url' && (
                              <PrimaryButton
                                variant='ghost'
                                size='sm'
                                onClick={() => window.open(mod?.contents[0]?.fileurl)}
                              >
                                <MdLink /> Truy cập
                              </PrimaryButton>
                            )}
                            {mod.modname === 'supervideo' && (
                              <PrimaryButton
                                variant='ghost'
                                size='sm'
                                onClick={() =>
                                  navigate(
                                    `${PATH.ELEARNING.STUDENT.VIDEO.replace(
                                      ':id',
                                      mod?.instance
                                    )}?m=${mod?.id}`
                                  )
                                }
                              >
                                <MdPlayArrow /> Phát
                              </PrimaryButton>
                            )}
                            {mod.modname === 'quiz' && (
                              <PrimaryButton
                                variant='ghost'
                                size='sm'
                                onClick={() =>
                                  navigate(
                                    `${PATH.ELEARNING.STUDENT.TEST_DETAIL.replace(
                                      ':id',
                                      mod?.instance
                                    )}?m=${mod?.id}&c=${course.id}`
                                  )
                                }
                              >
                                <MdQuiz /> Thực hiện
                              </PrimaryButton>
                            )}
                            {mod.modname === 'resource' && (
                              <PrimaryButton
                                variant='ghost'
                                size='sm'
                                onClick={() => {
                                  const url = `${
                                    mod?.contents[0]?.fileurl?.split('?')[0]
                                  }?token=${moodleToken}`;
                                  window.open(url);
                                }}
                              >
                                <IoMdEye /> Xem
                              </PrimaryButton>
                            )}
                            {mod.modname === 'page' && (
                              <PrimaryButton
                                key={mod?.contents[0]?.fileurl}
                                variant='ghost'
                                size='sm'
                                onClick={() =>
                                  navigate(
                                    `${PATH.ELEARNING.STUDENT.BOOK.replace(
                                      ':id',
                                      mod?.instance
                                    )}?m=${mod?.id}&url=${mod?.contents[0]?.fileurl}`
                                  )
                                }
                              >
                                <FaBookReader /> Đọc
                              </PrimaryButton>
                            )}
                            {mod?.modname === 'book' &&
                              mod?.contents?.length > 0 && (
                                <PrimaryButton
                                  variant='ghost'
                                  size='sm'
                                  onClick={() =>
                                    navigate(
                                      `${PATH.ELEARNING.STUDENT.BOOK.replace(
                                        ':id',
                                        mod?.instance
                                      )}?m=${mod?.id}&i=${mod.instance}&urls=${mod?.contents
                                        ?.filter((c) => c?.filename === 'index.html')
                                        ?.map((content) => content?.fileurl)
                                        ?.join(',')}`
                                    )
                                  }
                                >
                                  <FaBookReader /> Học
                                </PrimaryButton>
                              )}
                          </ModuleActionCol>
                        </ModuleRow>
                      ))}
                      {lesson?.summary && <LessonSummary>{lesson.summary}</LessonSummary>}
                    </LessonList.Item>
                  ))}
                </LessonList>
              </div>
            </Collapse>
          </Col>
        </Row>
      </Card.Body>
    </CardWrap>
  );
};

export default CourseCard;

/* ─── styled (DS tokens) ─── */

// Wrap BS Card với DS shadow + radius để khớp visual với các page khác
// đã adopt design system. BS Card vẫn giữ vì LMS DS chưa có Card primitive.
const CardWrap = styled(Card)`
  /* margin-bottom bỏ — parent Row.g-* đã quản khoảng cách giữa các
     CourseCard. Trước đây cộng cả g-4 (24px gutter) + margin-bottom
     (16px) = 40px gap, quá rộng. */
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  transition: box-shadow 200ms ease, transform 200ms ease;

  &:hover {
    box-shadow: ${shadow.md};
  }
`;

const CourseImage = styled.img`
  width: 100%;
  border-radius: ${radius.md};
`;

const CourseTitle = styled.h5`
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.semibold};
  color: ${colors.gray900};
  margin-bottom: ${spacing[2]};
`;

const CourseSummary = styled.div`
  font-size: ${typography.size.sm};
  color: ${colors.gray500};
  line-height: 1.5;
  margin-bottom: ${spacing[3]};
`;

const ModuleBadge = styled(Badge).attrs({ bg: 'primary' })`
  background: ${colors.primarySoft || colors.primary} !important;
  color: ${colors.primary} !important;
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.medium};
  padding: ${spacing[1]} ${spacing[2]};
  margin-right: ${spacing[1]};
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const LessonList = styled(ListGroup)`
  margin-top: ${spacing[3]};
  border-radius: ${radius.md};
  overflow: hidden;

  .list-group-item {
    border-color: ${colors.border};
  }
`;
LessonList.Item = ListGroup.Item;

const LessonSectionTitle = styled.h6`
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.semibold};
  color: ${colors.gray800};
  margin-bottom: ${spacing[2]};
`;

const ModuleRow = styled(Row).attrs({ className: 'align-items-center g-2' })`
  margin-bottom: ${spacing[2]};
`;

// Module name col: fill phần còn lại sau khi action col co theo content.
// `xs` (no value) ở Bootstrap = col-fill, không phải col-12 → giữ button
// trên cùng hàng ở mobile.
const ModuleNameCol = styled(Col)`
  min-width: 0; /* cho phép text ellipsis bên trong flex/grid container */
`;

// Action button col: col-auto cả 2 breakpoint → co theo content, luôn nằm
// bên phải tên module. Không wrap xuống dòng kể cả mobile.
const ModuleActionCol = styled(Col)`
  text-align: end;
  white-space: nowrap;
`;

const ModuleNameInner = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  min-width: 0;
`;

const ModuleName = styled.span`
  font-size: ${typography.size.sm};
  color: ${colors.gray700};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const LessonSummary = styled.p`
  font-size: ${typography.size.sm};
  color: ${colors.gray500};
  margin: ${spacing[2]} 0 0;
`;
